import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import { useQueryParam } from 'use-query-params';
import React, { useEffect, useRef, useState } from 'react';
import { MoreVert } from '@material-ui/icons';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Tab } from '@headlessui/react';
import Modal from 'react-modal';

import {
  AccountInfo,
  Badge,
  BusinessEntities,
  BusinessEntityLinks,
  FamilyMembers,
  HistoryNote,
  ProfileFines,
  ProfileNote,
} from '@project/components';
import {
  useAccountInfoQuery,
  useBusiness,
  useCallContactCheckStatus,
  useIndividual,
} from '@project/hooks/profile';
import {
  adminDailyCallsService,
  adminProfilesService,
  adminUserService,
  blackListApiService,
  referralCampaignsService,
  referralsService,
  userService,
  welcomeEmailService,
} from '@project/services';
import { useAutosizeTextArea, useDownloadFile } from '@project/hooks/global';
import { AddBlackListProps, Guid, ProfileType } from '@project/types';
import { classNames } from '@project/helpers';
import { useUserInfo } from '@project/react-queries';

Modal.setAppElement('#root');

const AccountsScreen: React.FC = () => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const history = useHistory();
  const queryClient = useQueryClient();
  const userInfo = useUserInfo();

  const [individualNote, setIndividualNote] = useState<string>('');
  const [historyNote, setHistoryNote] = useState<string>('');
  const [fleetNote, setFleetNote] = useState<string>('');

  const [userId] = useQueryParam<Guid>('Id');

  const [activeProfile, setActiveProfile] = useState<ProfileType>(ProfileType.Individual);

  const business = useBusiness(userId);
  const individual = useIndividual(userId);
  const accountInfoQuery = useAccountInfoQuery(userId, activeProfile);
  const doNotContactQuery = useCallContactCheckStatus(userId);

  useEffect(() => {
    if (accountInfoQuery.data?.invalidEmail === true) {
      toast.error('User Email Invalid');
    }
  }, [accountInfoQuery.data?.invalidEmail]);

  const userReferralsData =
    useQuery(['userReferrals', userId], () => referralsService.getIndividualReferrals(userId), {
      enabled: !!userId,
    })?.data ?? null;
  const referralCampaignDiscountData =
    useQuery('referralCampaignDiscount', referralCampaignsService.getActiveReferralCampaignDiscount)
      ?.data ?? 0;
  const userAvailableReferralsDiscount =
    userReferralsData?.filter(
      (referral) =>
        referralsService.referralStatusMapArray.find((m) => m.value === 'Accepted').enum ===
        referral.status.toString(),
    ).length * referralCampaignDiscountData;

  useAutosizeTextArea(textAreaRef.current, individualNote);

  const accountProfilesQuery = useQuery(
    ['AdminAccountProfileData', userId],
    () => adminProfilesService.getAccountProfiles(userId),
    {
      enabled: !!userId,
      onSuccess: (data) => {
        setHistoryNote(data?.historyNote);

        if (individualNote === '') {
          setIndividualNote(data?.individual?.note);
        }
        if (fleetNote === '') {
          setFleetNote(data?.business?.note);
        }

        if (data?.business?.businesses && data?.business?.businesses.length > 0) {
          toast.success('Fleet account available', {
            duration: 4000,
          });
        }
      },
    },
  );

  const useSendInvitationEmailMutation = useMutation(
    () => welcomeEmailService.send(accountInfoQuery.data.email),
    {
      onSuccess: () => {
        toast.success('Successfully sent invitation');
      },
    },
  );

  const useRemoveCallListMutation = useMutation(
    () => adminDailyCallsService.removeFromCallList(accountInfoQuery.data.id),
    {
      onSuccess: () => {
        toast.success('Successfully Removed User');
      },
      onError: () => {
        toast.error('User is Archived or Not Found');
      },
    },
  );

  const useAddCallListMutation = useMutation(
    () => adminDailyCallsService.removeFromCallList(accountInfoQuery.data.id),
    {
      onSuccess: () => {
        toast.success('Successfully Added User');
      },
      onError: () => {
        toast.error('User is Archived or Not Found');
      },
    },
  );

  const useSetIndividualNoteMutation = useMutation(
    () =>
      adminProfilesService.setProfileNote({
        note: individualNote,
        profileId: individual.id,
        isHuman: true,
      }),
    {
      onError: () => {
        toast.error('An error occurred updating profile note');
      },
    },
  );

  const useSetFleetEntityNoteMutation = useMutation(
    () =>
      adminProfilesService.setProfileNote({
        note: fleetNote,
        profileId: business.fleetId,
        isHuman: false,
      }),
    {
      onError: () => {
        toast.error('An error occurred updating profile note');
      },
    },
  );

  const useDeletePortalUserMutation = useMutation(
    'deletePortalUserMutation',
    () => userService.deletePortalUserAccount(userId),
    {
      onSuccess: () => {
        toast.success('Successfully deleted user');
        history.goBack();
      },
      onError: () => {
        toast.error('An error occurred deleting Portal User');
      },
    },
  );

  const useArchiveProfileMutation = useMutation(
    'archiveProfile',
    () => adminProfilesService.update(userId, { ...accountInfoQuery.data, isArchived: true }),
    {
      onSuccess: () => {
        toast.success('Successfully archived user');
        const timestamp = new Date().toISOString().split('T')[0];
        const archiveMessage = `\nUser Archived by ${userInfo.data.email} on ${timestamp}\n`;
        if (fleetNote === '') {
          setIndividualNote(individualNote + archiveMessage);
          useSetIndividualNoteMutation.mutate();
        } else if (individualNote === '') {
          setFleetNote(fleetNote + archiveMessage);
          useSetFleetEntityNoteMutation.mutate();
        }
        history.goBack();
      },
      onError: () => {
        toast.error('An error occurred archived Portal User');
      },
    },
  );

  const useUnlockProfileMutation = useMutation(
    'unlockProfile',
    () => adminProfilesService.update(userId, { ...accountInfoQuery.data, unlock: true }),
    {
      onSuccess: () => {
        toast.success('Successfully unlocked user');
        queryClient.invalidateQueries(['AdminAccountProfileData', userId]);
      },
      onError: () => {
        toast.error('An error occurred unlocking Portal User');
      },
    },
  );

  const useLockProfileMutation = useMutation(
    'lockProfile',
    () => adminProfilesService.update(userId, { ...accountInfoQuery.data, unlock: false }),
    {
      onSuccess: () => {
        toast.success('Successfully locked user');
        queryClient.invalidateQueries(['AdminAccountProfileData', userId]);
      },
      onError: () => {
        toast.error('An error occurred locking Portal User');
      },
    },
  );

  const useUnVerifyProfileMutation = useMutation(
    'unVerifyProfile',
    () => adminProfilesService.update(userId, { ...accountInfoQuery.data, isVerified: false }),
    {
      onSuccess: () => {
        toast.success('Successfully unverified user');
        queryClient.invalidateQueries(['AdminAccountProfileData', userId]);
      },
      onError: () => {
        toast.error('An error occurred unverified Portal User');
      },
    },
  );

  const useVerifyProfileMutation = useMutation(
    'verifyProfile',
    () => adminProfilesService.update(userId, { ...accountInfoQuery.data, isVerified: true }),
    {
      onSuccess: () => {
        toast.success('Successfully verified user');
        queryClient.invalidateQueries(['AdminAccountProfileData', userId]);
      },
      onError: () => {
        toast.error('An error occurred verifying Portal User');
      },
    },
  );

  const useUnarchiveProfileMutation = useMutation(
    'unarchiveProfile',
    () => adminProfilesService.update(userId, { ...accountInfoQuery.data, isArchived: false }),
    {
      onSuccess: () => {
        toast.success('Successfully un-archived user');
        const timestamp = new Date().toISOString().split('T')[0];
        const archiveMessage = `\nUser Un-Archived by ${userInfo.data.email} on ${timestamp}\n`;
        if (fleetNote === '') {
          setIndividualNote(individualNote + archiveMessage);
          useSetIndividualNoteMutation.mutate();
        } else if (individualNote === '') {
          setFleetNote(fleetNote + archiveMessage);
          useSetFleetEntityNoteMutation.mutate();
        }
        queryClient.invalidateQueries(['AdminAccountProfileData', userId]);
      },
      onError: () => {
        toast.error('An error occurred un-archived Portal User');
      },
    },
  );

  const { mutate: mutateIsWhatsAppVerified } = useMutation(
    'updateIsWhatsAppVerified',
    ([updateUserId, isWhatsAppVerified]: [Guid, boolean]) =>
      userService.updateIsWhatsAppVerified(updateUserId, isWhatsAppVerified),
    {
      onSuccess: () => {
        toast.success('WhatsApp verification has successfully updated');
        queryClient.invalidateQueries(['AdminAccountProfileData', userId]);
      },
      onError: () => {
        toast.error('WhatsApp verification failed to update');
      },
    },
  );

  const onClickToggleIsWhatsAppVerified = () => {
    mutateIsWhatsAppVerified([accountInfoQuery.data.id, !accountInfoQuery.data.isWhatsAppVerified]);
  };

  const { mutate: mutateBlackListUser } = useMutation(
    'updateBlackList',
    ([userIdNumber]: [AddBlackListProps]) => blackListApiService.add(userIdNumber),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['AdminAccountProfileData', userId]);
        const timestamp = new Date().toISOString().split('T')[0];
        const blackListMessage = `\nUser Black Listed by ${userInfo.data.email} on ${timestamp}\n`;
        if (fleetNote === '') {
          setIndividualNote(individualNote + blackListMessage);
          useSetIndividualNoteMutation.mutate();
        } else if (individualNote === '') {
          setFleetNote(fleetNote + blackListMessage);
          useSetFleetEntityNoteMutation.mutate();
        }
        toast.success('User Successfully Black Listed');
      },
      onError: () => {
        toast.error('User failed to be Black Listed');
      },
    },
  );

  const { mutate: mutateRemoveBlackListUser } = useMutation(
    'updateBlackList',
    ([userIdNumber]: [AddBlackListProps]) =>
      blackListApiService.removeBlackList(userIdNumber.userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['AdminAccountProfileData', userId]);
        const timestamp = new Date().toISOString().split('T')[0];
        const blackListMessage = `\nUser removed from Black List by ${userInfo.data.email} on ${timestamp}\n`;
        if (fleetNote === '') {
          setIndividualNote(individualNote + blackListMessage);
          useSetIndividualNoteMutation.mutate();
        } else if (individualNote === '') {
          setFleetNote(fleetNote + blackListMessage);
          useSetFleetEntityNoteMutation.mutate();
        }
        toast.success('User Successfully Removed from Black List');
      },
      onError: () => {
        toast.error('User failed to be Removed from Black List');
      },
    },
  );

  const onClickBlackListUser = () => {
    mutateBlackListUser([{ userId: accountProfilesQuery.data.offenderIdentifier.value }]);
  };

  const onClickRemoveBlackListUser = () => {
    mutateRemoveBlackListUser([{ userId: accountProfilesQuery.data.id }]);
  };

  const copyLinkToClipboard = (link: string) => {
    navigator.clipboard.writeText(link).then(() => toast.success(`Link copied to clipboard.`));
  };

  const resetPassword = useMutation(() => adminUserService.generateResetPasswordLink(userId), {
    onSuccess: (apiResult) => {
      copyLinkToClipboard(apiResult.link);
    },
    onError: () => {
      toast.error('An error occurred while generating the reset password link. Please try again.');
    },
  });
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);

  const [showMenu, setShowMenu] = useState(false);

  const { download: downloadPaidFinesCsv } = useDownloadFile({
    apiDefinition: () =>
      adminProfilesService.exportProfileFinesToCsv(
        activeProfile === ProfileType.Individual ? userId : business.activeBusinessEntityId,
        activeProfile === ProfileType.Individual,
        true,
      ),
    onSuccess: () => setShowMenu(false),
    onError: () => {
      toast.error('Could not export fines');
    },
    fileName: 'paid-fines-export.csv',
  });

  const { download: downloadUnPaidFinesCsv } = useDownloadFile({
    apiDefinition: () =>
      adminProfilesService.exportProfileFinesToCsv(
        activeProfile === ProfileType.Individual ? userId : business.activeBusinessEntityId,
        activeProfile === ProfileType.Individual,
        false,
      ),
    onSuccess: () => setShowMenu(false),
    onError: () => {
      toast.error('Could not export fines');
    },
    fileName: 'unpaid-fines-export.csv',
  });
  const menuButton = () => {
    const handleMenuClose = () => {
      setAnchorElement(null);
      setShowMenu(false);
    };

    const _copyResetPasswordLinkToClipboard = () => {
      if (!resetPassword.isSuccess) {
        resetPassword.mutate();
        setShowMenu(false);
      } else if (resetPassword.data) {
        copyLinkToClipboard(resetPassword.data.link);
        setShowMenu(false);
      } else {
        resetPassword.mutate();
        setShowMenu(false);
      }
    };

    const _deleteUserAccount = () => {
      if (window.confirm('Are you sure you wish to delete this account?')) {
        useDeletePortalUserMutation.mutate();
      }
    };

    const _archiveUserAccount = () => {
      if (window.confirm('Are you sure you wish to archive this account?')) {
        useArchiveProfileMutation.mutate();
      }
    };

    const _unlockUserAccount = () => {
      if (window.confirm('Are you sure you wish to unlock this account?')) {
        useUnlockProfileMutation.mutate();
      }
    };

    const _lockUserAccount = () => {
      if (window.confirm('Are you sure you wish to lock this account?')) {
        useLockProfileMutation.mutate();
      }
    };

    const _unVerifyUserAccount = () => {
      if (window.confirm('Are you sure you wish to mark the account as Unverified?')) {
        useUnVerifyProfileMutation.mutate();
      }
    };

    const _verifyUserAccount = () => {
      if (window.confirm('Are you sure you wish to mark the account as Verified?')) {
        useVerifyProfileMutation.mutate();
      }
    };

    const _unarchiveUserAccount = () => {
      if (window.confirm('Are you sure you wish to unarchive this account?')) {
        useUnarchiveProfileMutation.mutate();
      }
    };

    const _handleSendInvite = () => {
      if (window.confirm('Are you sure you wish to send an invite to this account?')) {
        useSendInvitationEmailMutation.mutate();
      }
    };

    const _handleRemoveCallList = () => {
      if (window.confirm('Are you sure you wish to remove this account from the call list?')) {
        useRemoveCallListMutation.mutate();
      }
    };

    const _handleAddCallList = () => {
      if (window.confirm('Are you sure you wish to add this account to the call list?')) {
        useAddCallListMutation.mutate();
      }
    };

    const handleMenuButtonClick = (event: React.MouseEvent<HTMLElement>) => {
      const anchorLocation = event.currentTarget;
      setShowMenu(true);
      setAnchorElement(anchorLocation);
    };

    return (
      <div className="absolute right-0">
        <div>
          <IconButton
            className="float-right"
            id="options-menu-button"
            aria-controls={showMenu ? 'options-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={showMenu ? 'true' : undefined}
            onClick={handleMenuButtonClick}
          >
            <MoreVert />
          </IconButton>
          <div>
            <Menu
              id="options-menu"
              anchorEl={anchorElement}
              open={showMenu}
              keepMounted
              onClose={handleMenuClose}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={downloadUnPaidFinesCsv}>Export Unpaid Fines</MenuItem>
              <MenuItem onClick={downloadPaidFinesCsv}>Export Paid Fines</MenuItem>
              <MenuItem onClick={_copyResetPasswordLinkToClipboard}>Reset Password Link</MenuItem>
              <MenuItem onClick={_handleSendInvite}>Send Invitation</MenuItem>
              {doNotContactQuery?.data ? (
                <MenuItem className="text-green-500" onClick={_handleAddCallList}>
                  Add To Call List
                </MenuItem>
              ) : (
                <MenuItem className="text-tertiary" onClick={_handleRemoveCallList}>
                  Remove From Call List
                </MenuItem>
              )}
              {accountInfoQuery?.data?.isArchived ? (
                <MenuItem className="text-green-500" onClick={_unarchiveUserAccount}>
                  Unarchive Account
                </MenuItem>
              ) : (
                <MenuItem className="text-tertiary" onClick={_archiveUserAccount}>
                  Archive Account
                </MenuItem>
              )}
              {accountInfoQuery?.data?.isLocked ? (
                <MenuItem className="text-green-500" onClick={_unlockUserAccount}>
                  Unlock Account
                </MenuItem>
              ) : (
                <MenuItem className="text-tertiary" onClick={_lockUserAccount}>
                  Lock Account
                </MenuItem>
              )}
              {accountInfoQuery?.data?.isVerified ? (
                <MenuItem className="text-tertiary" onClick={_unVerifyUserAccount}>
                  Mark as Unverified
                </MenuItem>
              ) : (
                <MenuItem className="text-green-500" onClick={_verifyUserAccount}>
                  Verify Account
                </MenuItem>
              )}
              {accountInfoQuery?.data?.isWhatsAppVerified ? (
                <MenuItem className="text-tertiary" onClick={onClickToggleIsWhatsAppVerified}>
                  Toggle WhatsApp as Unverified
                </MenuItem>
              ) : (
                <MenuItem className="text-green-500" onClick={onClickToggleIsWhatsAppVerified}>
                  Toggle WhatsApp as Verified
                </MenuItem>
              )}
              {!accountInfoQuery?.data?.disabled ? (
                <MenuItem className="text-tertiary" onClick={onClickBlackListUser}>
                  Black List User
                </MenuItem>
              ) : (
                <MenuItem className="text-green-500" onClick={onClickRemoveBlackListUser}>
                  Remove User From Black List
                </MenuItem>
              )}
              <MenuItem className="text-tertiary" onClick={_deleteUserAccount}>
                Delete Account
              </MenuItem>
            </Menu>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-grow mx-4">
      {menuButton()}
      <Tab.Group selectedIndex={activeProfile} onChange={setActiveProfile}>
        <Tab.List className="flex items-center">
          {['Individual', 'Business'].map((value) => (
            <Tab
              key={value}
              className={({ selected }) =>
                classNames(
                  'rounded-md p-2.5 text-base font-medium leading-5 my-2 mx-1',
                  'ring-white ring-opacity-60 ring-offset-2 focus:outline-none',
                  selected ? 'bg-secondary text-white shadow' : 'text-black-100 hover:bg-gray-200',
                )
              }
            >
              {value}
            </Tab>
          ))}
          {accountProfilesQuery.data?.doNotContact ? (
            <Badge value="Opted Out Of Emails" variant="danger" />
          ) : null}
          {doNotContactQuery?.data ? <Badge value="Opted Out Of Calls" variant="danger" /> : null}
          {accountProfilesQuery.data?.individual.syntellExportSyncActive ? (
            <Badge value="Syntell Export Active" variant="success" />
          ) : null}
          {accountProfilesQuery.data?.isArchived ? (
            <Badge value="Archived" variant="danger" />
          ) : null}
        </Tab.List>
        <div className="flex items-end">
          <span className="bg-total-green mb-4 p-2 shadow-lg rounded-sm float-right text-xs text-white font-bold">
            R{userAvailableReferralsDiscount} referral credit available.
          </span>
        </div>
        <Tab.Panels>
          <Tab.Panel>
            <div className="flex flex-row gap-x-4 mb-3">
              <AccountInfo
                profileType={activeProfile}
                userId={userId}
                accountInfoQuery={accountInfoQuery}
              />
              <FamilyMembers
                hasIndividualProfile={individual.hasIndividualProfile}
                individualId={individual.id}
                familyMembers={individual.familyMembers}
              />
              <ProfileNote
                isLoading={accountProfilesQuery.isLoading}
                ref={textAreaRef}
                value={individualNote}
                onChange={(e) => setIndividualNote(e.target.value)}
                onBlur={() => useSetIndividualNoteMutation.mutate()}
              />
              <HistoryNote
                isLoading={accountProfilesQuery.isLoading}
                ref={textAreaRef}
                value={historyNote}
              />
            </div>
          </Tab.Panel>
          <Tab.Panel>
            <div className="flex flex-row gap-x-4 mb-3">
              <AccountInfo
                profileType={activeProfile}
                userId={userId}
                accountInfoQuery={accountInfoQuery}
              />
              <BusinessEntities
                activeEntityId={business.activeBusinessEntityId}
                setActiveBusinessEntityId={business.setActiveBusinessEntityId}
                businessEntities={business.businessEntities}
                fleetId={business.fleetId}
                businessOwnerId={business.ownerId}
              />
              <BusinessEntityLinks
                businessEntityLinks={business.businessEntityLinks}
                entityCount={business.businessEntities.length}
                activeBusinessEntityId={business.activeBusinessEntityId}
              />
              <ProfileNote
                isLoading={accountProfilesQuery.isLoading}
                ref={textAreaRef}
                value={fleetNote}
                onChange={(e) => setFleetNote(e.target.value)}
                onBlur={() => useSetFleetEntityNoteMutation.mutate()}
              />
              <HistoryNote
                isLoading={accountProfilesQuery.isLoading}
                ref={textAreaRef}
                value={historyNote}
              />
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
      <ProfileFines
        userId={userId}
        profileType={activeProfile}
        businessEntityId={business.activeBusinessEntityId}
      />
    </div>
  );
};

export default AccountsScreen;
